// App.tsx — Public Frontend Router
// public-frontend/src/App.tsx
import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import NavBar from './components/NavBar'
import Footer from './components/Footer'
import HomePage from './pages/HomePage'
import ProductsPage from './pages/ProductsPage'
import ContactPage from './pages/ContactPage'
import LoginPage from "./pages/LoginFormEnhanced";
import RegistrationForm from "./pages/RegistrationForm";
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'
import PricingPage from './pages/PricingPage'
import NotificationBanner from './components/NotificationBanner'
import HumanGate from './components/HumanGate'
import { isHumanVerified } from './utils/humanPass'

// ─── Config ────────────────────────────────────────────────────────────────────
// ✅ CORS-safe base handed to every page as the `apiBase` prop.
//
// ⚠️ Do NOT restore `VITE_API_BASE || "http://localhost:3001"`. Production's
// public-frontend/.env sets VITE_API_URL='' and never defines VITE_API_BASE, so
// that form resolved to the literal http://localhost:3001 in the SHIPPED bundle
// — the contact form (ContactUs.tsx, which DOES use this prop) was calling a
// server on each visitor's own machine, which Chrome answers with a "wants to
// access apps and services on this device" prompt before failing.
//
// '' → relative '/api/...' → same origin via nginx (prod) or the Vite proxy
// (dev). LoginFormEnhanced and RegistrationForm ignore this prop and read
// VITE_API_URL directly; they now agree with it rather than working around it.
const API_BASE = import.meta.env.VITE_API_URL ?? "";
let PRODUCT_URL: string
if (!import.meta.env.VITE_NODE_ENV || import.meta.env.VITE_NODE_ENV === 'dev') {
  PRODUCT_URL = 'http://localhost:5173/stocksignalspro#home'
} else {
  PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || 'https://stocksignalspro.larxnet.com/'
}

// ─── Types ─────────────────────────────────────────────────────────────────────
export interface UserData {
  id: number
  email: string
  username: string
  isAdmin?: boolean
}

export interface Notification {
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  show: boolean
}

// ─── App ───────────────────────────────────────────────────────────────────────
const App: React.FC = () => {
  const navigate = useNavigate()

  // Dark mode — default ON, matching current product
  const [dark, setDark] = useState<boolean>(() => {
    const saved = localStorage.getItem('pub_darkMode')
    return saved !== null ? JSON.parse(saved) : true
  })

  // Home-page "I am human" gate (components/HumanGate.tsx), same as the product
  // frontend. Skipped for anyone holding a human pass and for a browser that
  // already has an auth token (a signed-in user is not asked to prove it).
  const [humanVerified, setHumanVerified] = useState<boolean>(() => {
    if (isHumanVerified()) return true
    try {
      return !!localStorage.getItem('authToken')
    } catch {
      return false
    }
  })

  const [notification, setNotification] = useState<Notification>({
    message: '',
    type: 'info',
    show: false,
  })

  // Apply dark class to html element
  useEffect(() => {
    const html = document.documentElement
    if (dark) {
      html.classList.add('dark')
      document.body.classList.remove('light')
    } else {
      html.classList.remove('dark')
      document.body.classList.add('light')
    }
    localStorage.setItem('pub_darkMode', JSON.stringify(dark))
  }, [dark])

  const showNotification = useCallback(
    (message: string, type: Notification['type'] = 'info') => {
      setNotification({ message, type, show: true })
      setTimeout(() => setNotification((n) => ({ ...n, show: false })), 5000)
    },
    []
  )

  // ─── Auth Handlers ──────────────────────────────────────────────────────────
  const handleLoginSuccess = useCallback(
    async (token: string, userData: UserData, refreshToken?: string) => {
      // Persist auth so product frontend can pick it up
      localStorage.setItem('authToken', token)
      localStorage.setItem('user', JSON.stringify(userData))
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      showNotification(`Welcome back, ${userData.username}! Redirecting to the platform...`, 'success')
      // Give a moment for the token to be saved, then redirect
      setTimeout(() => {
        window.location.href = PRODUCT_URL
      }, 1500)
    },
    [showNotification]
  )

  const handleRegisterSuccess = useCallback(
    async (token: string, userData: UserData, refreshToken?: string) => {
      localStorage.setItem('authToken', token)
      localStorage.setItem('user', JSON.stringify(userData))
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      showNotification(`Welcome, ${userData.username}! Redirecting to the platform...`, 'success')
      setTimeout(() => {
        window.location.href = PRODUCT_URL
      }, 1500)
    },
    [showNotification]
  )

  // ─── Shared Props ───────────────────────────────────────────────────────────
  const sharedProps = { dark, apiBase: API_BASE, productUrl: PRODUCT_URL }

  return (
    <div
      className={`min-h-screen flex flex-col transition-colors duration-300 ${
        dark ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <NavBar dark={dark} setDark={setDark} />

      <main className='flex-1'>
        <Routes>
          <Route
            path='/'
            element={
              humanVerified ? (
                <HomePage {...sharedProps} />
              ) : (
                <HumanGate dark={dark} onVerified={() => setHumanVerified(true)} />
              )
            }
          />
          <Route path='/products' element={<ProductsPage {...sharedProps} />} />
          <Route path='/contact' element={<ContactPage {...sharedProps} />} />
          <Route
            path='/login'
            element={
              <LoginPage
                {...sharedProps}
                onLoginSuccess={handleLoginSuccess}
                onSwitchToRegister={() => navigate("/register")}
              />
            }
          />
          <Route
            path='/register'
            element={
              <RegistrationForm
                {...sharedProps}
                onRegisterSuccess={handleRegisterSuccess}
                onSwitchToLogin={() => navigate("/login")}
              />
            }
          />
          <Route path='/terms' element={<TermsOfService {...sharedProps} />} />
          <Route path='/privacy' element={<PrivacyPolicy {...sharedProps} />} />
          <Route path='/pricing' element={<PricingPage {...sharedProps} />} />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </main>

      <Footer dark={dark} />

      <NotificationBanner
        message={notification.message}
        type={notification.type}
        show={notification.show}
        onClose={() => setNotification((n) => ({ ...n, show: false }))}
      />
    </div>
  );
}

export default App
